import React, { useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Typography,
  Paper,
  useTheme,
  Tabs,
  Tab,
} from '@mui/material';
import {
  AttachFile,
  Image as ImageIcon,
  Link as LinkIcon,
  Code,
  Close,
  InsertEmoticon,
  FormatSize,
} from '@mui/icons-material';

const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = 'Compose your professional email here...',
  height = 300,
  minHeight = 200,
  onAttachFile,
  attachments = [],
  onRemoveAttachment,
  mergeTags = [],
  onInsertMergeTag,
  readOnly = false,
}) => {
  const theme = useTheme();
  const quillRef = useRef(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Contact');
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  // Custom toolbar configuration - Professional Full Featured
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ font: ['serif', 'monospace', 'sans-serif', 'arial', 'courier', 'georgia', 'helvetica', 'times'] }],
          [{ size: ['small', false, 'large', 'huge'] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ script: 'sub' }, { script: 'super' }],
          [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ direction: 'rtl' }],
          [{ align: [] }],
          ['blockquote', 'code-block'],
          ['link', 'image', 'video'],
          ['clean'],
        ],
        handlers: {
          link: () => {
            const quill = quillRef.current?.getEditor();
            if (quill) {
              const range = quill.getSelection();
              if (range) {
                const text = quill.getText(range.index, range.length);
                setLinkText(text);
                setLinkUrl('');
                setLinkDialogOpen(true);
              }
            }
          },
          image: () => {
            setImageUrl('');
            setImageDialogOpen(true);
          },
        },
      },
      clipboard: {
        matchVisual: false,
      },
      history: {
        delay: 1000,
        maxStack: 50,
        userOnly: true,
      },
    }),
    []
  );

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'script',
    'list',
    'bullet',
    'check',
    'indent',
    'direction',
    'align',
    'blockquote',
    'code-block',
    'link',
    'image',
    'video',
  ];

  const handleInsertLink = () => {
    const quill = quillRef.current?.getEditor();
    if (quill && linkUrl) {
      const range = quill.getSelection();
      if (range) {
        if (linkText) {
          quill.deleteText(range.index, range.length);
          quill.insertText(range.index, linkText, 'link', linkUrl);
        } else {
          quill.format('link', linkUrl);
        }
      }
      setLinkDialogOpen(false);
      setLinkUrl('');
      setLinkText('');
    }
  };

  const handleInsertImage = () => {
    const quill = quillRef.current?.getEditor();
    if (quill && imageUrl) {
      const range = quill.getSelection();
      if (range) {
        quill.insertEmbed(range.index, 'image', imageUrl);
      }
      setImageDialogOpen(false);
      setImageUrl('');
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const quill = quillRef.current?.getEditor();
        if (quill && e.target?.result) {
          const range = quill.getSelection() || { index: 0 };
          quill.insertEmbed(range.index, 'image', e.target.result);
          quill.setSelection(range.index + 1);
        }
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  };

  const handleInsertMergeTag = (tag) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection();
      if (range) {
        quill.insertText(range.index, tag);
        quill.setSelection(range.index + tag.length);
      }
      if (onInsertMergeTag) {
        onInsertMergeTag(tag);
      }
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0 && onAttachFile) {
      onAttachFile(files);
    }
    event.target.value = '';
  };

  return (
    <Box>
      {/* Merge Tags and Attachments Bar */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 1,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
          borderRadius: 1,
        }}
      >
        <Stack spacing={1.5}>
          {/* Merge Tags with Categories */}
          {mergeTags && mergeTags.length > 0 && (() => {
            const strip = (t) => t.replace(/{{/g, '').replace(/}}/g, '').trim();
            const categories = {
              Contact: ['Legal Name', 'DBA', 'Primary Contact', 'Secondary Contact', 'Phone', 'Cell Phone', 'Fax', 'Email', 'Physical Address', 'Mailing Address', 'State(Physical)', 'State(Mailing)'],
              Operations: ['Status', 'Carrier Operation', 'Entity Type', 'Entity Classification', 'Business Organization', 'Cargo Classification'],
              Fleet: ['Power Units', 'Drivers', 'Total CDL Drivers', 'Owned Trucks', 'Owned Tractors', 'Owned Trailers', 'Leased Trucks', 'Leased Tractors', 'Leased Trailers'],
              Dates: ['MCS-150 Date', 'Add Date', 'MCS-150 Mileage', 'MCS-150 Mileage Year', 'Active Duration'],
              Authority: ['Authority Common', 'Authority Broker', 'Authority Pending Common', 'Authority Pending Contract', 'Authority Pending Broker'],
              Insurance: ['Insurance Type', 'Insurance Class', 'Insurance Max Coverage', 'Insurance Policy Number', 'Insurance Effective Date', 'Insurance Company'],
            };

            const tagSet = new Set(mergeTags.map(strip));
            const grouped = {};
            Object.entries(categories).forEach(([label, list]) => {
              const groupTags = list.filter((key) => tagSet.has(key)).map((key) => `{{${key}}}`);
              if (groupTags.length) grouped[label] = groupTags;
            });
            const used = new Set(Object.values(grouped).flat().map(strip));
            const otherTags = mergeTags.filter((t) => !used.has(strip(t)));
            if (otherTags.length) grouped.Other = otherTags;

            const categoryKeys = Object.keys(grouped);
            const currentCategory = categoryKeys.includes(selectedCategory) ? selectedCategory : (categoryKeys[0] || 'Contact');
            const displayTags = grouped[currentCategory] || [];

            return (
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
                  Merge Tags (click to insert):
                </Typography>
                <Tabs
                  value={currentCategory}
                  onChange={(_, newValue) => setSelectedCategory(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 32,
                    mb: 1,
                    borderRadius: 1,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                    '& .MuiTabs-indicator': {
                      height: 2,
                      borderRadius: '2px 2px 0 0',
                    },
                    '& .MuiTab-root': {
                      minHeight: 32,
                      py: 0.5,
                      px: 1.5,
                      minWidth: 'auto',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      color: theme.palette.text.secondary,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                        color: theme.palette.primary.main,
                      },
                      '&.Mui-selected': {
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                      },
                    },
                    '& .MuiTabs-scrollButtons': {
                      width: 28,
                      '&.Mui-disabled': {
                        opacity: 0.3,
                      },
                    },
                  }}
                >
                  {categoryKeys.map((cat) => (
                    <Tab key={cat} label={cat} value={cat} />
                  ))}
                </Tabs>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5, maxHeight: 120, overflowY: 'auto' }}>
                  {displayTags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      onClick={() => handleInsertMergeTag(tag)}
                      sx={{
                        cursor: 'pointer',
                        fontSize: '0.7rem',
                        height: 24,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            );
          })()}

          {/* Quick Actions */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <input
              type="file"
              ref={fileInputRef}
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              accept="*/*"
            />
            <input
              type="file"
              ref={imageInputRef}
              onChange={handleImageUpload}
              style={{ display: 'none' }}
              accept="image/*"
            />
            <Tooltip title="Attach files">
              <Button
                size="small"
                variant="outlined"
                startIcon={<AttachFile />}
                onClick={() => fileInputRef.current?.click()}
                disabled={readOnly}
              >
                Attach Files
              </Button>
            </Tooltip>
            <Tooltip title="Insert image from computer">
              <Button
                size="small"
                variant="outlined"
                startIcon={<ImageIcon />}
                onClick={() => imageInputRef.current?.click()}
                disabled={readOnly}
              >
                Insert Image
              </Button>
            </Tooltip>
            <Tooltip title="Insert image from URL">
              <IconButton
                size="small"
                onClick={() => setImageDialogOpen(true)}
                disabled={readOnly}
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Attachments Display */}
          {attachments && attachments.length > 0 && (
            <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Attachments ({attachments.length}):
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                {attachments.map((file, index) => (
                  <Chip
                    key={index}
                    label={file.name || `File ${index + 1}`}
                    size="small"
                    onDelete={onRemoveAttachment ? () => onRemoveAttachment(index) : undefined}
                    icon={<AttachFile fontSize="small" />}
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </Paper>

      {/* Rich Text Editor */}
      <Box
        sx={{
          '& .quill': {
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
          },
          '& .ql-toolbar': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            borderBottom: `1px solid ${theme.palette.divider}`,
            padding: '8px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            '& .ql-formats': {
              marginRight: '8px',
              display: 'flex',
              gap: '2px',
            },
          },
          '& .ql-container': {
            minHeight: minHeight,
            maxHeight: height,
            fontSize: '14px',
            fontFamily: theme.typography.fontFamily,
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
            backgroundColor: theme.palette.background.paper,
          },
          '& .ql-editor': {
            minHeight: minHeight,
            maxHeight: height - 42,
            overflowY: 'auto',
            color: theme.palette.text.primary,
            padding: '16px',
            lineHeight: 1.6,
            '&.ql-blank::before': {
              color: theme.palette.text.secondary,
              fontStyle: 'italic',
            },
          },
          '& .ql-stroke': {
            stroke: theme.palette.text.primary,
          },
          '& .ql-fill': {
            fill: theme.palette.text.primary,
          },
          '& .ql-picker': {
            color: theme.palette.text.primary,
          },
          '& .ql-picker-label': {
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '4px',
            padding: '3px 6px',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
            },
          },
          '& .ql-picker-options': {
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '4px',
            boxShadow: theme.shadows[4],
          },
          '& button': {
            width: '28px',
            height: '28px',
            padding: '3px',
            borderRadius: '4px',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
            },
            '&.ql-active': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '& .ql-stroke': {
                stroke: theme.palette.primary.contrastText,
              },
              '& .ql-fill': {
                fill: theme.palette.primary.contrastText,
              },
            },
          },
          '& .ql-active .ql-stroke': {
            stroke: theme.palette.primary.contrastText,
          },
          '& .ql-active .ql-fill': {
            fill: theme.palette.primary.contrastText,
          },
          '& .ql-snow .ql-tooltip': {
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[4],
            color: theme.palette.text.primary,
            '& input': {
              color: theme.palette.text.primary,
              border: `1px solid ${theme.palette.divider}`,
            },
          },
          '& .ql-snow a': {
            color: theme.palette.primary.main,
          },
        }}
      >
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          readOnly={readOnly}
        />
      </Box>

      {/* Link Insert Dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Insert Link</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Link URL"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              autoFocus
            />
            <TextField
              fullWidth
              label="Link Text (optional)"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              placeholder="Click here"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleInsertLink} variant="contained" disabled={!linkUrl}>
            Insert
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Insert Dialog */}
      <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Insert Image</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Image URL"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
              autoFocus
            />
            <Typography variant="caption" color="text.secondary">
              Enter the URL of an image hosted online. For local images, use the attachment feature.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleInsertImage} variant="contained" disabled={!imageUrl}>
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RichTextEditor;
